import type { NetworkType } from '../../../utils/networkType';
import { apiFetch } from 'services/apiClient';
import type { RcdMeasurementsByNode } from '../types/rcdMeasurement';

const API_BASE = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:8082/api/v1').replace(/\/+$/, '');

export type EarthingMeasurementPointGroup =
  | 'MAIN_PANEL'
  | 'SUB_PANEL'
  | 'FIRE_PANEL'
  | 'SOCKET'
  | 'BODY';

export type EarthingEnvironmentCondition = 'DRY' | 'WET' | 'HUMID' | 'DUSTY';

export type EarthingCompliance = 'NOT_1' | 'NOT_2' | 'NOT_3' | 'NOT_4' | 'NOT_5' | 'COMPLIANT' | 'NON_COMPLIANT' | 'NOT_EVALUATED';
export type ManualEarthingCompliance = Extract<EarthingCompliance, 'NOT_1' | 'NOT_2' | 'NOT_3' | 'NOT_4' | 'NOT_5'>;
export type EarthingLimitCalculationStatus = 'CALCULATED' | 'MISSING_ENVIRONMENT' | 'INSUFFICIENT_DATA';

export type EarthingMeasurement = {
  clientKey?: string;
  id?: number;
  reportNodeId?: number;
  rowOrder?: number;
  measurementPointGroup?: EarthingMeasurementPointGroup | null;
  measurementPointDescription?: string | null;
  measurementEnvironmentCondition?: EarthingEnvironmentCondition | null;
  rcdRatingMa?: number | string | null;
  toroidRatingA?: number | string | null;
  protectiveConductorCrossSectionMm2?: number | string | null;
  ratedCurrentA?: number | string | null;
  tripCurveTypeOrModel?: string | null;
  phaseEarthLoopImpedanceOhm?: number | string | null;
  phaseEarthLoopImpedanceDash?: boolean;
  phaseNeutralLoopImpedanceOhm?: number | string | null;
  rcdTripCurrentMa?: number | string | null;
  rcdTripCurrentDidNotTrip?: boolean;
  rcdTripTimeMs?: number | string | null;
  rcdTripTimeDidNotTrip?: boolean;
  limitValueZsRsOhm?: number | string | null;
  limitValueCalculationStatus?: EarthingLimitCalculationStatus | null;
  /** Backend-calculated display value; excluded from save payloads. */
  generatedCompliance?: EarthingCompliance | null;
  compliance?: EarthingCompliance | null;
  complianceOverridden?: boolean;
};

export type EarthingMeasurementsByNode = Record<string, EarthingMeasurement[]>;

export type PanelControlDraftAnswers = {
  reportNodeId: number;
  sourceReportNodeId: number;
  sourceEarthingMeasurementId?: number;
  sourceEarthingClientKey?: string;
  answers: Record<string, unknown>;
};

export type ControlGroupAnswers = Record<string, Record<string, unknown>>;

export type ControlTaskControlGroup = {
  id: number;
  originControlGroupId?: number | null;
  reportNodeId: number;
  sourceEarthingMeasurementId: number;
  sourceReportNodeId: number;
  measurementPointGroup: EarthingMeasurementPointGroup;
  title: string;
  description?: string | null;
  sortOrder: number;
  archived: boolean;
  answers: Record<string, unknown>;
  answersWithMetadata?: ControlTaskAnswersPayload['answersWithMetadata'];
};

export type ControlTaskAnswersPayload = {
  controlGroups?: ControlTaskControlGroup[];
  earthingMeasurementIdsByClientKey?: Record<string, number>;
  id?: number;
  bulkGroupId?: number | null;
  responseKind?: string;
  sourceResponseId?: number | null;
  correctionCaseId?: number | null;
  revisionNo?: number | null;
  answers?: Record<string, any>;
  answersWithMetadata?: Array<{
    questionId?: number;
    questionTitle?: string;
    questionDescription?: string;
    questionCol?: number;
    answer?: any;
  }>;
  updatedAt?: string;
  reportFormatId?: number | null;
  reportFormatPublicUuid?: string | null;
  attributeStaticValues?: any[];
  toolStaticValues?: any[];
  selectedControlAreaId?: string | number | null;
  customControlAreaName?: string | null;
  notes?: Record<string, string>;
  inspectionNote?: string | null;
  inspectionNoteAttachmentDocumentIds?: number[];
  earthingMeasurementsByNode?: EarthingMeasurementsByNode;
  rcdMeasurementsByNode?: RcdMeasurementsByNode;
};

export type ControlTaskBulkAnswersPayload = {
  bulkGroupId?: number;
  proposalId?: number;
  taskId?: number;
  attributeId?: number;
  count?: number;
  responses?: ControlTaskAnswersPayload[];
};

export type ControlTaskBulkAnswersSavePayload = {
  bulkGroupId?: number;
  count: number;
  sharedAnswers?: Record<string, any>;
  answersByOrder?: Record<string, Record<string, any>>;
  selectedControlAreaId?: string | number | null;
  customControlAreaName?: string | null;
  notes?: Record<string, string>;
  inspectionNote?: string | null;
  inspectionNoteAttachmentDocumentIds?: number[];
  elapsedSeconds?: number;
  attributeStaticValues?: any[];
  toolStaticValues?: any[];
};

export type ControlTaskBulkGroup = {
  id: number;
  proposalId?: number;
  taskId?: number;
  attributeId?: number;
  reportCount?: number;
  savedResponseCount?: number;
  hasNonconformity?: boolean;
  createdAt?: string;
  updatedAt?: string;
};

export type ControlTaskBulkCapacity = {
  totalQuantity?: number;
  usedReportCount?: number;
  maximumReportCount?: number;
};

export type ControlTaskReportArchiveJob = {
  archiveId: string;
  status: 'PENDING' | 'PROCESSING' | 'READY' | 'FAILED';
  filename: string;
  downloadUrl?: string | null;
  errorMessage?: string | null;
};

export type ControlTaskReportApprovalRow = {
  responseId?: number | null;
  taskId?: number | null;
  appointmentId?: number | null;
  accountId?: number | null;
  inspectorName?: string | null;
  attributeId?: number | null;
  attributeName?: string | null;
  reportFormatId?: number | null;
  reportFormatPublicUuid?: string | null;
  orderNo?: number | null;
  hasResponse?: boolean;
  controlCompleted?: boolean | null;
  reportApproved?: boolean | null;
  reportUrl?: string | null;
  signedReportUrl?: string | null;
  reportNumber?: string | null;
  reportSequenceNo?: number | null;
  responseKind?: string | null;
  sourceResponseId?: number | null;
  correctionCaseId?: number | null;
  correctionStatus?: string | null;
  correctionResponseId?: number | null;
  followUpCaseId?: number | null;
  followUpStatus?: string | null;
  followUpResponseId?: number | null;
  hasCriticalNonconformity?: boolean | null;
  hasOpenCorrection?: boolean | null;
  hasOpenFollowUp?: boolean | null;
};

export type ControlTaskReportPublishResult = {
  earthingMeasurementIdsByClientKey?: Record<string, number>;
  responseId?: number | null;
  inspectionTaskId?: number | null;
  reportUrl?: string | null;
  filename?: string | null;
  url: string;
  objectUrl?: boolean;
};

export type CriticalNonconformityPayload = {
  questionId?: number;
  questionTitle?: string;
  answerTitle?: string;
  description?: string;
  nodeTitle?: string;
  sortOrder?: number;
  isTrue?: boolean | null;
  sourceAnswerSnapshot?: any;
};

export type ControlTaskCorrectionQuestion = {
  sourceControlGroupId?: number | null;
  id?: number;
  questionId?: number;
  questionTitle?: string;
  nodeTitle?: string;
  sourceAnswerSnapshot?: any;
  sourceAnswerTitle?: string;
  sourceAnswerDescription?: string;
  sourceIsTrue?: boolean | null;
  sortOrder?: number;
};

export type ControlTaskCorrection = {
  id?: number;
  proposalId?: number;
  sourceResponseId?: number;
  sourceTaskId?: number;
  sourceAttributeId?: number;
  sourceOrderNo?: number;
  appointmentId?: number | null;
  correctionTaskId?: number | null;
  correctionResponseId?: number | null;
  assignedAccountId?: number | null;
  assignedInspectorName?: string | null;
  caseType?: 'CORRECTION' | 'FOLLOW_UP' | string | null;
  status?: string | null;
  plannedStartDate?: string | null;
  plannedEndDate?: string | null;
  contractId?: string | null;
  contractUrl?: string | null;
  sourceReportNumber?: string | null;
  correctionReportNumber?: string | null;
  questions?: ControlTaskCorrectionQuestion[];
};

export type ControlTaskCorrectionFormPayload = {
  sourceControlGroupAnswers?: ControlGroupAnswers;
  correctionCase?: ControlTaskCorrection;
  questionIds?: number[];
  sourceAnswers?: Record<string, any>;
  correctionResponse?: ControlTaskAnswersPayload;
};

export type ControlTaskCorrectionComparison = {
  correctionCaseId?: number;
  sourceResponseId?: number;
  sourceReportNumber?: string | null;
  correctionResponseId?: number | null;
  correctionReportNumber?: string | null;
  rows?: Array<{
    questionId?: number;
    questionTitle?: string;
    nodeTitle?: string;
    beforeAnswer?: string;
    beforeDescription?: string;
    afterAnswer?: string;
    afterDescription?: string;
  }>;
};

export const fetchControlTaskAnswers = (
  proposalId: number,
  taskId: number,
  attributeId: number,
  order: number,
): Promise<ControlTaskAnswersPayload> =>
  apiFetch(
    `/control/${proposalId}/taskId/${taskId}/attribute/${attributeId}/order/${order}`,
  );

export const fetchControlTaskBulkAnswers = (
  proposalId: number,
  taskId: number,
  attributeId: number,
  bulkGroupId?: number,
): Promise<ControlTaskBulkAnswersPayload> =>
  apiFetch(
    `/control/${proposalId}/taskId/${taskId}/attribute/${attributeId}/bulk${bulkGroupId ? `?bulkGroupId=${bulkGroupId}` : ''}`,
  );

export const fetchControlTaskBulkGroups = (
  proposalId: number,
  taskId: number,
  attributeId: number,
): Promise<ControlTaskBulkGroup[]> =>
  apiFetch(`/control/${proposalId}/taskId/${taskId}/attribute/${attributeId}/bulk-groups`);

export const requestControlTaskReportArchive = (
  proposalId: number,
  accountIds?: number[],
): Promise<ControlTaskReportArchiveJob> =>
  apiFetch(`/control/${proposalId}/reports/archive`, {
    method: 'POST',
    headers: accountIds === undefined
      ? { Accept: 'application/json' }
      : { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: accountIds === undefined ? undefined : JSON.stringify({ accountIds }),
  });

export const fetchControlTaskReportArchiveStatus = (
  proposalId: number,
  archiveId: string,
): Promise<ControlTaskReportArchiveJob> =>
  apiFetch(`/control/${proposalId}/reports/archive/${encodeURIComponent(archiveId)}`);

export const fetchControlTaskBulkCapacity = (
  proposalId: number,
  taskId: number,
  attributeId: number,
  bulkGroupId?: number,
): Promise<ControlTaskBulkCapacity> =>
  apiFetch(
    `/control/${proposalId}/taskId/${taskId}/attribute/${attributeId}/bulk-capacity${bulkGroupId ? `?bulkGroupId=${bulkGroupId}` : ''}`,
  );

export const deleteControlTaskBulkGroup = (
  proposalId: number,
  taskId: number,
  attributeId: number,
  bulkGroupId: number,
): Promise<void> =>
  apiFetch(`/control/${proposalId}/taskId/${taskId}/attribute/${attributeId}/bulk-groups/${bulkGroupId}`, {
    method: 'DELETE',
  });

export const saveControlTaskAnswers = (
  proposalId: number,
  taskId: number,
  attributeId: number,
  order: number,
  answers: Record<string, any>,
  attributeStaticValues?: any[],
  toolStaticValues?: any[],
  selectedControlAreaId?: string | number | null,
  customControlAreaName?: string | null,
  notes?: Record<string, string>,
  inspectionNote?: string | null,
  elapsedSeconds?: number,
  inspectionNoteAttachmentDocumentIds: number[] = [],
  earthingMeasurementsByNode?: EarthingMeasurementsByNode,
  rcdMeasurementsByNode?: RcdMeasurementsByNode,
  controlGroupAnswers?: ControlGroupAnswers,
  panelControlDraftAnswers?: PanelControlDraftAnswers[],
): Promise<ControlTaskAnswersPayload> =>
  apiFetch(`/control/${proposalId}/taskId/${taskId}/attribute/${attributeId}/order/${order}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      answers,
      attributeStaticValues,
      toolStaticValues,
      selectedControlAreaId: selectedControlAreaId ?? null,
      customControlAreaName: customControlAreaName ?? null,
      notes,
      inspectionNote: inspectionNote ?? null,
      elapsedSeconds,
      inspectionNoteAttachmentDocumentIds,
      earthingMeasurementsByNode,
      rcdMeasurementsByNode,
      controlGroupAnswers,
      panelControlDraftAnswers,
    }),
  });

export const saveControlTaskBulkAnswers = (
  proposalId: number,
  taskId: number,
  attributeId: number,
  payload: ControlTaskBulkAnswersSavePayload,
): Promise<ControlTaskBulkAnswersPayload> =>
  apiFetch(`/control/${proposalId}/taskId/${taskId}/attribute/${attributeId}/bulk`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

const buildControlTaskAnswerBody = (
  answers: Record<string, any>,
  attributeStaticValues?: any[],
  toolStaticValues?: any[],
  selectedControlAreaId?: string | number | null,
  customControlAreaName?: string | null,
  notes?: Record<string, string>,
  inspectionNote?: string | null,
  elapsedSeconds?: number,
  inspectionNoteAttachmentDocumentIds: number[] = [],
  controlGroupAnswers?: ControlGroupAnswers,
) => JSON.stringify({
  answers,
  attributeStaticValues,
  toolStaticValues,
  selectedControlAreaId: selectedControlAreaId ?? null,
  customControlAreaName: customControlAreaName ?? null,
  notes,
  inspectionNote: inspectionNote ?? null,
  elapsedSeconds,
  inspectionNoteAttachmentDocumentIds,
  controlGroupAnswers,
});

export const deleteControlTaskAnswers = (
  proposalId: number,
  taskId: number,
  attributeId: number,
  order: number,
): Promise<void> =>
  apiFetch(`/control/${proposalId}/taskId/${taskId}/attribute/${attributeId}/order/${order}`, {
    method: 'DELETE',
  });

export const publishControlTaskReport = (
  proposalId: number,
  taskId: number,
  attributeId: number,
  order: number,
  answers: Record<string, any>,
  selectedControlAreaId?: string | number | null,
  customControlAreaName?: string | null,
  notes?: Record<string, string>,
  inspectionNote?: string | null,
  elapsedSeconds?: number,
  inspectionNoteAttachmentDocumentIds: number[] = [],
  bulkGroupId?: number,
  earthingMeasurementsByNode?: EarthingMeasurementsByNode,
  rcdMeasurementsByNode?: RcdMeasurementsByNode,
  controlGroupAnswers?: ControlGroupAnswers,
  panelControlDraftAnswers?: PanelControlDraftAnswers[],
): Promise<ControlTaskReportPublishResult> => {
  const token = localStorage.getItem('accessToken');
  if (!token) {
    throw new Error('Missing access token');
  }

  const bulkGroupQuery = bulkGroupId
    ? `?bulkGroupId=${encodeURIComponent(String(bulkGroupId))}`
    : '';

  return fetch(
    `${API_BASE}/control/${proposalId}/taskId/${taskId}/attribute/${attributeId}/order/${order}/publish-report${bulkGroupQuery}`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        answers,
        selectedControlAreaId: selectedControlAreaId ?? null,
        customControlAreaName: customControlAreaName ?? null,
        notes,
        inspectionNote: inspectionNote ?? null,
        elapsedSeconds,
        inspectionNoteAttachmentDocumentIds,
        earthingMeasurementsByNode,
        rcdMeasurementsByNode,
        controlGroupAnswers,
        panelControlDraftAnswers,
      }),
    },
  ).then(async (response) => {
    if (!response.ok) {
      const errorText = await response.text();
      let message = `Failed to publish report: ${response.status}`;
      if (errorText) {
        try {
          const parsed = JSON.parse(errorText);
          const serverMessage = typeof parsed?.message === 'string' ? parsed.message : errorText;
          message = `${message} - ${serverMessage}`;
        } catch {
          message = `${message} - ${errorText}`;
        }
      }
      throw new Error(message);
    }

    const result = await response.json();
    if (!result?.reportUrl) {
      throw new Error('Failed to publish report: report URL is missing');
    }
    return {
      ...result,
      url: result.reportUrl,
      objectUrl: false,
    };
  });
};

export const publishBulkControlTaskReport = (
  proposalId: number,
  taskId: number,
  attributeId: number,
  order: number,
  bulkGroupId: number,
): Promise<ControlTaskReportPublishResult> =>
  publishControlTaskReport(
    proposalId,
    taskId,
    attributeId,
    order,
    {},
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    [],
    bulkGroupId,
  );

export const downloadControlTaskReport = async (
  proposalId: number,
  taskId: number,
  attributeId: number,
  order: number,
  bulkGroupId?: number,
): Promise<{ blob: Blob; url: string }> => {
  const token = localStorage.getItem('accessToken');
  if (!token) {
    throw new Error('Missing access token');
  }
  const query = bulkGroupId ? `?bulkGroupId=${encodeURIComponent(String(bulkGroupId))}` : '';

  const response = await fetch(
    `${API_BASE}/control/${proposalId}/taskId/${taskId}/attribute/${attributeId}/order/${order}/report.pdf${query}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/pdf',
      },
    },
  );

  if (!response.ok) {
    const errorText = await response.text();
    let message = `Failed to download report: ${response.status}`;
    if (errorText) {
      try {
        const parsed = JSON.parse(errorText);
        const serverMessage = typeof parsed?.message === 'string' ? parsed.message : errorText;
        message = `${message} - ${serverMessage}`;
      } catch {
        message = `${message} - ${errorText}`;
      }
    }
    throw new Error(message);
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  return { blob, url };
};

export const listControlTaskReportApprovals = (
  proposalId: number,
): Promise<ControlTaskReportApprovalRow[]> =>
  apiFetch(`/control/proposal/${proposalId}/report-approvals`);

export const approveControlTaskReport = (
  proposalId: number,
  taskId: number,
  attributeId: number,
  order: number,
): Promise<ControlTaskReportApprovalRow> =>
  apiFetch(`/control/${proposalId}/taskId/${taskId}/attribute/${attributeId}/order/${order}/approve-report`, {
    method: 'POST',
  });

export const revokeControlTaskReportApproval = (
  proposalId: number,
  taskId: number,
  attributeId: number,
  order: number,
): Promise<ControlTaskReportApprovalRow> =>
  apiFetch(`/control/${proposalId}/taskId/${taskId}/attribute/${attributeId}/order/${order}/approve-report`, {
    method: 'DELETE',
  });

export const uploadSignedControlTaskReport = async (
  proposalId: number,
  taskId: number,
  attributeId: number,
  order: number,
  file: File | Blob,
): Promise<ControlTaskReportApprovalRow> => {
  const token = localStorage.getItem('accessToken');
  if (!token) {
    throw new Error('Missing access token');
  }

  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(
    `${API_BASE}/control/${proposalId}/taskId/${taskId}/attribute/${attributeId}/order/${order}/signed-report`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    },
  );

  if (!response.ok) {
    const errorText = await response.text();
    let message = `Failed to upload signed report: ${response.status}`;
    if (errorText) {
      try {
        const parsed = JSON.parse(errorText);
        message = typeof parsed?.message === 'string' ? parsed.message : errorText;
      } catch {
        message = errorText;
      }
    }
    throw new Error(message);
  }

  return response.json();
};

export const listControlTaskCriticalNonconformities = (
  proposalId: number,
  taskId: number,
  attributeId: number,
  order: number,
): Promise<CriticalNonconformityPayload[]> =>
  apiFetch(`/control/${proposalId}/taskId/${taskId}/attribute/${attributeId}/order/${order}/critical-nonconformities`);

export const createControlTaskCorrection = (payload: {
  proposalId?: number;
  sourceResponseId?: number | null;
  sourceTaskId?: number | null;
  sourceAttributeId?: number | null;
  sourceOrderNo?: number | null;
  assignedAccountId?: number | null;
  caseType?: 'CORRECTION' | 'FOLLOW_UP' | string | null;
  plannedStartDate?: string | null;
  plannedEndDate?: string | null;
  actorAccountId?: number | null;
  contractId?: string | null;
  contractUrl?: string | null;
  note?: string | null;
}): Promise<ControlTaskCorrection> =>
  apiFetch('/control/corrections', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

export const createControlTaskCorrections = (payload: {
  proposalId?: number;
  assignedAccountId?: number | null;
  caseType?: 'CORRECTION' | 'FOLLOW_UP' | string | null;
  plannedStartDate?: string | null;
  plannedEndDate?: string | null;
  actorAccountId?: number | null;
  contractId?: string | null;
  contractUrl?: string | null;
  note?: string | null;
  sources?: Array<{
    sourceResponseId?: number | null;
    sourceTaskId?: number | null;
    sourceAttributeId?: number | null;
    sourceOrderNo?: number | null;
  }>;
}): Promise<ControlTaskCorrection[]> =>
  apiFetch('/control/corrections/bulk', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

export const fetchControlTaskCorrection = (caseId: number): Promise<ControlTaskCorrection> =>
  apiFetch(`/control/corrections/${caseId}`);

export const deleteControlTaskCorrection = (caseId: number): Promise<void> =>
  apiFetch(`/control/corrections/${caseId}`, {
    method: 'DELETE',
  });

export const fetchControlTaskCorrectionForm = (caseId: number): Promise<ControlTaskCorrectionFormPayload> =>
  apiFetch(`/control/corrections/${caseId}/form`);

export const saveControlTaskCorrectionAnswers = (
  caseId: number,
  answers: Record<string, any>,
  attributeStaticValues?: any[],
  toolStaticValues?: any[],
  selectedControlAreaId?: string | number | null,
  customControlAreaName?: string | null,
  notes?: Record<string, string>,
  inspectionNote?: string | null,
  elapsedSeconds?: number,
  inspectionNoteAttachmentDocumentIds: number[] = [],
  controlGroupAnswers?: ControlGroupAnswers,
): Promise<ControlTaskAnswersPayload> =>
  apiFetch(`/control/corrections/${caseId}/answers`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: buildControlTaskAnswerBody(
      answers,
      attributeStaticValues,
      toolStaticValues,
      selectedControlAreaId,
      customControlAreaName,
      notes,
      inspectionNote,
      elapsedSeconds,
      inspectionNoteAttachmentDocumentIds,
      controlGroupAnswers,
    ),
  });

export const publishControlTaskCorrectionReport = (
  caseId: number,
  answers: Record<string, any>,
  selectedControlAreaId?: string | number | null,
  customControlAreaName?: string | null,
  notes?: Record<string, string>,
  inspectionNote?: string | null,
  elapsedSeconds?: number,
  inspectionNoteAttachmentDocumentIds: number[] = [],
  controlGroupAnswers?: ControlGroupAnswers,
): Promise<ControlTaskReportPublishResult> => {
  const token = localStorage.getItem('accessToken');
  if (!token) {
    throw new Error('Missing access token');
  }

  return fetch(`${API_BASE}/control/corrections/${caseId}/publish-report`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/pdf',
      'Content-Type': 'application/json',
    },
    body: buildControlTaskAnswerBody(
      answers,
      undefined,
      undefined,
      selectedControlAreaId,
      customControlAreaName,
      notes,
      inspectionNote,
      elapsedSeconds,
      inspectionNoteAttachmentDocumentIds,
      controlGroupAnswers,
    ),
  }).then(async (response) => {
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `Failed to publish correction report: ${response.status}`);
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    return {
      url,
      filename: `control-task-correction-${caseId}.pdf`,
      objectUrl: true,
    };
  });
};

export const approveControlTaskCorrectionReport = (
  caseId: number,
): Promise<ControlTaskReportApprovalRow> =>
  apiFetch(`/control/corrections/${caseId}/approve-report`, {
    method: 'POST',
  });

export const revokeControlTaskCorrectionReportApproval = (
  caseId: number,
): Promise<ControlTaskReportApprovalRow> =>
  apiFetch(`/control/corrections/${caseId}/approve-report`, {
    method: 'DELETE',
  });

export const fetchControlTaskCorrectionComparison = (
  caseId: number,
): Promise<ControlTaskCorrectionComparison> =>
  apiFetch(`/control/corrections/${caseId}/comparison`);

/** Draft calculation only. Persisted automatic values are still recalculated by the backend. */
export type RcdMappingPreview = {
  finalCircuitPanelName: string | null;
  rcdType: string | null;
  ratedResidualCurrentMa: string | null;
  testTripTimeMs: string | null;
  testTripTimeDidNotTrip: boolean;
  complianceCalculationStatus: string;
  generatedComplianceResult?: string | null;
  complianceCalculationMessage?: string;
  phaseEarthLoopImpedanceOhm?: string | null;
  phaseNeutralLoopImpedanceOhm?: string | null;
  calculatedThreePhaseShortCircuitCurrentKa?: string | null;
};

export const previewRcdMapping = (
  source: EarthingMeasurement, signal?: AbortSignal,
  context?: { networkType?: NetworkType; phaseNeutralLoopImpedanceOhm?: number | string | null;
    neutralToEarthVoltageV?: number | string | null; zeroingDetected?: boolean },
): Promise<RcdMappingPreview> => apiFetch('/control/rcd/mapping-preview', {
  method: 'POST', signal,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({
    networkType: context?.networkType ?? null,
    zeroingDetected: context?.zeroingDetected === true,
    neutralToEarthVoltageV: context?.neutralToEarthVoltageV === '' ? null : context?.neutralToEarthVoltageV ?? null,
    ratedCurrentA: source.ratedCurrentA === '' ? null : source.ratedCurrentA ?? null,
    tripCurveTypeOrModel: source.tripCurveTypeOrModel?.trim() || null,
    measurementEnvironmentCondition: source.measurementEnvironmentCondition ?? null,
    rcdRatingMa: source.rcdRatingMa === '' ? null : source.rcdRatingMa ?? null,
    protectiveConductorCrossSectionMm2: source.protectiveConductorCrossSectionMm2 === '' ? null : source.protectiveConductorCrossSectionMm2 ?? null,
    measurementPointDescription: source.measurementPointDescription ?? null,
    measurementPointGroup: source.measurementPointGroup ?? null,
    phaseEarthLoopImpedanceOhm: source.phaseEarthLoopImpedanceOhm === '' ? null : source.phaseEarthLoopImpedanceOhm ?? null,
    phaseNeutralLoopImpedanceOhm: context?.phaseNeutralLoopImpedanceOhm === ''
      ? null : context?.phaseNeutralLoopImpedanceOhm ?? null,
    rcdTripCurrentMa: source.rcdTripCurrentMa === '' ? null : source.rcdTripCurrentMa ?? null,
    rcdTripCurrentDidNotTrip: source.rcdTripCurrentDidNotTrip ?? false,
    toroidRatingA: source.toroidRatingA === '' ? null : source.toroidRatingA ?? null,
    rcdTripTimeMs: source.rcdTripTimeMs === '' ? null : source.rcdTripTimeMs ?? null,
    rcdTripTimeDidNotTrip: source.rcdTripTimeDidNotTrip ?? false,
  }),
});
